import { sportslineGraphQLClient } from '@lib/graphql-client';
import { PickSnippetInput, SportslineWebComponent_PickSnippetQuery } from '@lib/graphql-types';
import { Component, Host, Prop, State, h, Element } from '@stencil/core';
import { isGameAvailableForBetting } from '@utils/bettingUtils';
import { generateHexId } from '@utils/commonUtils';
import { observeVisibilityAndTrigger } from '@utils/html';
import { fromPickSnippetAttributeToTrackingImpressionWidgetDataBets, fromPickSnippetAttributeToTrackingViewedWidgetDataBets, toPickSnippetInput } from '@utils/pickSnippet';
import { fromPickSnippetToPickSnippetCardProps, getDataFromAttribute } from '@utils/sidelineUtils';
import { eventsName, PickSnippetEventViewed, trackEvent } from '@utils/trackingUtils';

export type PickSnippetArgsData = {
  line?: number | null;
  selectionId: string;
  teamAbbr?: null | string;
  teamId?: number | null;
  vendorGameId: string;
  vendorMarketId: string;
  gameId: string;
  marketId: number;
  marketName: string;
  marketType: 'team' | 'player' | 'game' | 'standard';
  odds: number;
  pick: 'over' | 'under' | 'yes' | 'no' | 'home' | 'away' | 'draw' | 'standard' | 'unknown';
  playerId?: string;
  playerName?: string;
  type: 'total' | 'spread' | 'moneyline' | 'overUnder' | 'yesNo';
  vendorPlayerId?: string;
  vendorTeamId?: string;
};

export type SelectionIds = {
  gameId: string;
  playerId: string | null;
  teamId: string | null;
  vendorGameId: string;
  vendorPlayerId?: string;
  vendorTeamId?: string;
};

@Component({
  tag: 'sportsline-pick-snippet',
  styleUrl: 'sportsline-pick-snippet.css',
  shadow: true,
})
export class SportslinePickSnippet {
  @State() data: SportslineWebComponent_PickSnippetQuery['pickSnippet'] = null;
  @State() loading: boolean = true;
  @State() error: string | null = null;
  @State() initialized: boolean = false;

  @Element() el: HTMLSportslinePickSnippetElement;

  /**
   * Input for the pick snippet.
   * This should contain the necessary fields to fetch the pick data.
   */
  @Prop() pick: PickSnippetInput;

  private instanceId: string = generateHexId();
  private version: string = '1.0.0';
  private name: string = 'pick-snippet';
  private client = sportslineGraphQLClient();

  private get getWidgetDataInfo() {
    return {
      name: this.name,
      instanceId: this.instanceId,
      version: this.version,
      ref: this.el,
    };
  }

  private trackError(error: Error) {
    this.error = `Error fetching data: ${error.message}`;
    console.warn('Error fetching data:', error);
    trackEvent({
      action: 'error',
      eventName: eventsName.martech,
      payload: {
        error: this.error,
      },
      widgetData: this.getWidgetDataInfo,
    });
  }

  private async fetch(input: PickSnippetInput) {
    this.initialized = true; // Mark as initialized to allow rendering
    this.loading = true;
    this.error = null;


    // fetching data, before loading the component
    if (!Boolean(input)) {
      console.warn('No input provided for SportslinePickSnippet');
      this.loading = false;
      this.error = 'No input provided for SportslinePickSnippet';
      return Promise.resolve();
    }

    return this.client.dataFetcher
      .SportslineWebComponent_PickSnippet({
        input,
      })
      .then(response => {
        const data = response.data;
        if (Boolean(response.errors?.length)) {
          throw new Error(`GraphQL errors: ${response.errors.map(error => error.message).join(', ')}`);
        }
        if (!Boolean(data?.pickSnippet)) {
          throw new Error('No pick snippet data found');
        }
        this.data = data.pickSnippet;
        const dataAttr = getDataFromAttribute(this.getDataPickAttribute);
        const event: PickSnippetEventViewed = {
          action: 'viewed',
          eventName: eventsName.martech,
          widgetData: {
            ...this.getWidgetDataInfo,
            bets: [
              fromPickSnippetAttributeToTrackingViewedWidgetDataBets({
                dataAttr,
                pick: data.pickSnippet,
              }),
            ],
          },
        };
        trackEvent(event);
      });
  }

  async componentWillLoad() {
    try {
      const input = this.getInput;
      if (!Boolean(input)) {
        throw new Error('Invalid input provided for SportslinePickSnippet');
      }
      // Track the initial impression event
      trackEvent({
        action: 'impression',
        eventName: eventsName.martech,
        widgetData: {
          ...this.getWidgetDataInfo,
          bets: [
            fromPickSnippetAttributeToTrackingImpressionWidgetDataBets({
              dataAttr: getDataFromAttribute(this.getDataPickAttribute),
            }),
          ],
        },
      });
      // Fetch data when the component is about to be visible
      observeVisibilityAndTrigger({
        element: this.el,
        callback: async () => {
          await this.fetch(input)
            .catch((error: Error) => {
              // Track error if fetching fails
              this.trackError(error);
            })
            .finally(() => {
              this.loading = false;
              this.initialized = true; // Mark as initialized after fetching
            });
        },
      });
    } catch (error) {
      this.trackError(error);
      this.loading = false;
      this.initialized = true; // Mark as initialized after fetching
    }
  }

  private get renderVendor() {
    if (this.el.getElementsByClassName('cbs-widget').length > 0) {
      return;
    }
    if (!isGameAvailableForBetting(this.data?.game) || !Boolean(this.getData?.sideLinesData)) return;
    const sidelines = document.createElement('div');
    sidelines.classList.add('cbs-widget');
    // sidelines.innerHTML = `BET NOW`;
    sidelines.setAttribute('data-config', JSON.stringify({...this.getData.sideLinesData , instanceId : this.instanceId}));
    this.el.appendChild(sidelines);
  }

  private get getDataPickAttribute() {
    return this.el.getAttribute('data-pick') ?? null;
  }

  // Remove typing and place correct one
  private get getDataFromAttribute() {
    return getDataFromAttribute(this.getDataPickAttribute);
  }

  private get getInput() {
    const dataPick = this.getDataFromAttribute;
    if (dataPick !== null) {
      try {
        return toPickSnippetInput(dataPick);
      } catch (error) {
        console.warn('Invalid input format for SportslinePickSnippet:', error);
        return null;
      }
    }
    return this.pick;
  }

  private get getData() {
    return fromPickSnippetToPickSnippetCardProps({
      data: this.data,
      dataAttr: this.getDataPickAttribute,
    });
  }

  render() {
    if (!this.initialized) return null; // Prevent rendering before initialization

    if (this.loading) {
      return (
        <Host>
          <div class="sportsline-pick-snippet--loading">
            <sportsline-circular-progress size="sm" />
          </div>
        </Host>
      );
    }
    if (Boolean(this.error)) {
      return null; // or you can render an error message if needed
      // return <Host>{this.instanceId}</Host>; // or you can render an error message if needed
    }
    const data = this.getData;
    if (!data) return null;

    const { gameInfoLabel, type, teamId, teamName, playerId, playerName, activeColor, label, marketLabel, odds, side } = this.getData;
    return (
      <Host>
        {/* {this.instanceId} */}
        <sportsline-pick-snippet-card
          gameInfoLabel={gameInfoLabel}
          type={type}
          playerId={playerId}
          teamId={teamId}
          teamName={teamName}
          playerName={playerName}
          activeColor={activeColor}
          label={label}
          marketLabel={marketLabel}
          odds={odds}
          side={side}
        >
          {/* Not Pretty but works */}
          <slot>{this.renderVendor}</slot>
        </sportsline-pick-snippet-card>
      </Host>
    );
  }
}
